import { useEffect, useState } from "react";
import { fetchDocument } from "../../Service/restapi/sharedContractService";
import { LoadingAnimation } from "../../Service/ResourceAdapter";
import DocumentAccepted from "./DocumentAccepted";
import PdfDownload from "../PdfDownload";
import DocumentPositionList from "../Positions/DocumentPositionList";
import SubjectDate from "../SubjectDate";
import ProfileContactInfoTopRight from "../Contact/ProfileContactInfoTopRight";
import { ResourceContextProvider } from "../../Service/Context/ResourceContext";
import FooterContact from "../FooterContact";
import DocumentPartialInvoices from "./DocumentPartialInvoices";
import { Space, Text } from "@mantine/core";
import ProfileInjectWrapper from "../Profile/ProfileInjectWrapper";
import VatNote from "./VatNote";
import LetterHead from "../Contact/LetterHead";
import InvoiceQrCodes from "./InvoiceQrCodes";
import {
  getDocumentTranslator,
  translateGeneratedDocumentText,
} from "./documentLanguage";
import { useLingui } from "@lingui/react";
import {
  CancelFormSubmissionAction,
  CancelledFormSubmissionNotice,
} from "../FormSubmissionCancellation";

const Document = ({
  resource,
  formSubmission,
  reloadFormSubmission,
  values,
  handleChange,
  patchSubmissionValue,
}) => {
  const [document, setDocument] = useState(undefined);
  const { i18n } = useLingui();

  useEffect(() => {
    getContract();
  }, [resource]);

  function getContract() {
    fetchDocument(resource.entityId)
      .then((resp) => {
        if (resp.ok) {
          resp.json().then((contract) => {
            setDocument(contract);
          });
        } else {
          console.error("could not fetch document " + resource.entityId);
        }
      })
      .catch((error) => {
        console.error({ error });
        console.error("could not fetch document " + resource.entityId);
      });
  }

  if (!document) {
    return <LoadingAnimation />;
  }

  const { subject, number } = document;
  const t = getDocumentTranslator(i18n, document.language);
  const translatedSubject =
    document.documentType === "INVOICE"
      ? document.partialInvoices?.length > 0
        ? t("finalInvoiceLabel")
        : t("invoiceLabel")
      : document.documentType === "PARTIAL_INVOICE"
        ? t("partialInvoiceLabel")
        : document.documentType === "DELIVERYNOTE"
          ? t("deliveryNoteLabel")
          : document.documentType === "PACKING_LIST"
            ? t("packingListLabel")
            : translateGeneratedDocumentText(subject, t);
  const isOffer = document.documentType === "OFFER";
  const isAccepted =
    formSubmission.lastSubmitted !== null &&
    formSubmission.lastSubmitted !== undefined;
  const isCancelled =
    formSubmission.cancelledAt !== null &&
    formSubmission.cancelledAt !== undefined;
  const finalSubject = `${translatedSubject} ${
    number ? `${t("documentNumberPrefix")} ${number}` : ""
  }`;

  return (
    <ResourceContextProvider
      resourceId={resource.id}
      contactId={document.contactId}
      customerContactId={document.customerId}
    >
      {isOffer && isCancelled && (
        <CancelledFormSubmissionNotice
          cancelledAt={formSubmission.cancelledAt}
          documentType="OFFER"
          t={t}
        />
      )}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <LetterHead
          isCustomer
          withPartner={true}
          documentType={document.documentType}
        />
        <div style={{ textAlign: "right" }}>
          <ProfileContactInfoTopRight
            contactId={document.contactId}
            resourceId={resource.id}
            language={document.language}
            documentType={document.documentType}
          />
        </div>
      </div>
      <br />
      <SubjectDate subject={finalSubject} date={document.date} />
      {(document.documentType === "REMINDER" ||
        document.documentType === "CANCEL") &&
        document.originalInvoiceNumber && (
          <Text mb="md" weight="bold">
            {t("originalInvoiceNumber")}: {document.originalInvoiceNumber}
          </Text>
        )}
      <div dangerouslySetInnerHTML={{ __html: document.headRow }} />

      <DocumentPositionList
        formSubmission={formSubmission}
        resourceId={resource.id}
        values={values}
        handleChange={handleChange}
        patchSubmissionValue={patchSubmissionValue}
        documentId={document.id}
        document={document}
      />
      <ProfileInjectWrapper resourceId={resource.id}>
        <DocumentPartialInvoices bookitupDocument={document} />
      </ProfileInjectWrapper>
      <Space h={30} />
      <VatNote language={document.language} />
      <div dangerouslySetInnerHTML={{ __html: document.endRow }} />
      <InvoiceQrCodes invoice={document} />
      <Space h={50} />
      <FooterContact
        documentType={document.documentType}
        language={document.language}
      />
      {isOffer && !isCancelled && (
        <DocumentAccepted
          document={document}
          formSubmission={formSubmission}
          values={values}
          patchSubmissionValue={patchSubmissionValue}
          reload={reloadFormSubmission}
          resource={resource}
        />
      )}
      {!isCancelled && (isAccepted || !isOffer) && (
        <>
          <Space h="xl" />
          <PdfDownload
            formSubmission={formSubmission}
            text={`${translatedSubject} ${t("downloadSuffix")}`}
          />
        </>
      )}
      {isOffer && isAccepted && !isCancelled && (
        <CancelFormSubmissionAction
          documentType="OFFER"
          formSubmissionId={formSubmission.id}
          lastSubmitted={formSubmission.lastSubmitted}
          reloadFormSubmission={reloadFormSubmission}
          t={t}
        />
      )}
    </ResourceContextProvider>
  );
};

export default Document;
